import React, { useState, Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export default function AddVehicleModal({ isOpen, onClose, vehicle, onSubmit }) {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    registrationNumber: '',
    vehicleType: '',
    driverName: '',
    driverPhone: '',
  });

  useEffect(() => {
    if (vehicle) {
      setFormData({
        registrationNumber: vehicle.registrationNumber || '',
        vehicleType: vehicle.vehicleType || '',
        driverName: vehicle.driverName || '',
        driverPhone: vehicle.driverPhone || '',
      });
    } else {
      setFormData({
        registrationNumber: '',
        vehicleType: '',
        driverName: '',
        driverPhone: '',
      });
    }
  }, [vehicle]);

  const validateRegistrationNumber = (value) => {
    const regPattern = /^[A-Z]{2}\d{2}[A-Z]{1,2}\d{1,4}$/;
    return regPattern.test(value);
  };

  const validatePhone = (value) => {
    const phonePattern = /^[6-9]\d{9}$/;
    return phonePattern.test(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    
    if (!validateRegistrationNumber(formData.registrationNumber)) {
      newErrors.registrationNumber = 'Invalid format. Example: KL06G5521 or KL07AD4392';
    }

    if (!validatePhone(formData.driverPhone)) {
      newErrors.driverPhone = 'Invalid phone number. Must be 10 digits starting with 6, 7, 8, or 9';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error saving vehicle:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    if (field === 'registrationNumber' || field === 'driverPhone') {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    let finalValue = value;
    if (field === 'registrationNumber') {
      finalValue = value.toUpperCase();
    } else if (field === 'driverPhone') {
      finalValue = value.replace(/\D/g, '');
    }
    
    setFormData(prev => ({ ...prev, [field]: finalValue }));
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 dark:bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full sm:max-w-md transform overflow-hidden bg-white dark:bg-gray-800 
                p-4 sm:p-6 text-left align-middle shadow-xl transition-all rounded-t-2xl sm:rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                  >
                    {vehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500 sm:hidden"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Registration Number
                      </label>
                      <input
                        type="text"
                        value={formData.registrationNumber}
                        onChange={(e) => handleChange('registrationNumber', e.target.value)}
                        className={`w-full px-4 py-2.5 rounded-lg bg-white dark:bg-gray-700 border 
                          ${errors.registrationNumber ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}
                          text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400
                          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
                        placeholder="KL06G5521"
                        required
                      />
                      {errors.registrationNumber && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.registrationNumber}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Vehicle Type
                      </label>
                      <input
                        type="text"
                        value={formData.vehicleType}
                        onChange={(e) => handleChange('vehicleType', e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 
                          text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400
                          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Driver Name
                      </label>
                      <input
                        type="text"
                        value={formData.driverName}
                        onChange={(e) => handleChange('driverName', e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 
                          text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400
                          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Driver Phone
                      </label>
                      <input
                        type="tel"
                        value={formData.driverPhone}
                        onChange={(e) => handleChange('driverPhone', e.target.value)}
                        className={`w-full px-4 py-2.5 rounded-lg bg-white dark:bg-gray-700 border 
                          ${errors.driverPhone ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}
                          text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400
                          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
                        placeholder="9876543210"
                        required
                      />
                      {errors.driverPhone && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.driverPhone}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                    <button
                      type="button"
                      onClick={onClose}
                      className="w-full sm:w-auto px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 
                        bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg
                        hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full sm:w-auto px-4 py-2.5 text-sm font-medium text-white 
                        bg-primary-600 rounded-lg border border-transparent
                        hover:bg-primary-700 
                        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent 
                        disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Saving...' : (vehicle ? 'Update Vehicle' : 'Add Vehicle')}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}